import { useQuery } from '@tanstack/react-query';
import { Gate } from '../components/shell/gate.tsx';
import { Card, CardContent } from '../components/ui/card.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { Skeleton } from '../components/ui/skeleton.tsx';
import { describeError } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { api, type RecordCountsView } from '../lib/client.ts';

const NUMBER = new Intl.NumberFormat('en-US');
const fmt = (n: number) => NUMBER.format(n);

type CustomFields = RecordCountsView['candidates']['customFields'];
type Line = { label: string; value: string };

const customLine = (c: CustomFields): Line => ({
  label: 'Custom fields',
  value: `${fmt(c.definitions)} ${c.definitions === 1 ? 'field' : 'fields'} · ${fmt(c.values)} ${c.values === 1 ? 'value' : 'values'}`,
});

/** One record kind: its live count and what hangs off it. */
function KindCard({
  title,
  count,
  lines,
  children,
}: {
  title: string;
  count: number;
  lines: Line[];
  children?: React.ReactNode;
}) {
  const slug = title.toLowerCase();
  return (
    <Card data-testid={`count-${slug}`}>
      <CardContent className="space-y-4">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="text-[17px] font-bold tracking-tight">{title}</h3>
          <span className="text-2xl font-bold tabular-nums" data-testid={`count-${slug}-total`}>
            {fmt(count)}
          </span>
        </div>
        <dl className="divide-border divide-y text-sm">
          {lines.map((l) => (
            <div className="flex justify-between gap-4 py-2" key={l.label}>
              <dt className="text-muted-foreground shrink-0">{l.label}</dt>
              <dd className="text-right font-medium tabular-nums">{l.value}</dd>
            </div>
          ))}
        </dl>
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * Record counts: how many live records of each kind the organisation holds, as the model's tree
 * (Candidates; Projects → Deals and Jobs; Companies), each with its custom fields (definitions
 * the viewer may see, and filled values) and notes. One read, `GET /stats/record-counts`.
 */
export function RecordCountsPage() {
  const session = useSession();
  const q = useQuery({
    queryKey: ['stats', 'record-counts'],
    queryFn: api.stats.recordCounts,
    // The Gate below refuses the page; do not ask for what the API would refuse too.
    enabled: session.can('record_count.read'),
  });
  const c = q.data;

  return (
    <Gate permission="record_count.read">
      <PageHeader
        title="Record counts"
        description="How many records of each kind the organization holds. Deleted records are not counted."
      />
      {q.isError && (
        <p className="text-destructive text-sm" role="alert">
          {describeError(q.error)}
        </p>
      )}
      {q.isPending && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      )}
      {c && (
        <div className="grid items-start gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            <KindCard
              title="Candidates"
              count={c.candidates.count}
              lines={[
                customLine(c.candidates.customFields),
                { label: 'Notes', value: fmt(c.candidates.notes) },
              ]}
            />
            <KindCard
              title="Companies"
              count={c.companies.count}
              lines={[customLine(c.companies.customFields)]}
            />
          </div>
          <KindCard
            title="Projects"
            count={c.projects.count}
            lines={[
              customLine(c.projects.customFields),
              { label: 'Notes', value: fmt(c.projects.notes) },
            ]}
          >
            <div className="bg-muted/60 space-y-4 rounded-xl p-4">
              <KindCard
                title="Deals"
                count={c.deals.count}
                lines={[
                  customLine(c.deals.customFields),
                  { label: 'Notes', value: fmt(c.deals.notes) },
                ]}
              />
              <KindCard
                title="Jobs"
                count={c.jobs.count}
                lines={[
                  customLine(c.jobs.customFields),
                  { label: 'Notes', value: fmt(c.jobs.notes) },
                  { label: 'Shortlists', value: fmt(c.jobs.shortlists) },
                  { label: 'Shortlisted candidates', value: fmt(c.jobs.shortlistedCandidates) },
                ]}
              />
            </div>
          </KindCard>
        </div>
      )}
    </Gate>
  );
}
